<script setup lang="ts">
  import { onMounted, ref, onBeforeUnmount, computed } from 'vue';
  import { Features } from '@shared/features';
  import { useSetCookie, useGetCookie } from '@storefront/storefront';
  import { useShared } from '@othercommerce/foundation';

  declare global {
    interface WindowEventMap {
      beforeinstallprompt: BeforeInstallPromptEvent;
    }
  }

  type PromptResult = {
    outcome: 'accepted' | 'dismissed',
  }

  type BeforeInstallPromptEvent = Event & {
    prompt: () => Promise<PromptResult>,
  }

  const shared = useShared();
  const name = computed(() => shared.value.appName || 'Promoznawcy');
  const cookieTime = computed(() => shared.value.appCookieTime || 180);
  const cookieName = computed(() => name.value + '_pwa_install');
  const installation = ref<BeforeInstallPromptEvent>();
  const visible = ref(false);
  const outcome = ref();

  const open = computed(() => {
    return Features.pwa.isEnabled && visible.value && outcome.value === undefined;
  });

  function onBeforeInstallPrompt(event: BeforeInstallPromptEvent) {
    event.preventDefault();

    installation.value = event;
    visible.value = true;
  }

  function onInstall() {
    if (installation.value) {
      installation.value.prompt().then((result) => {
        outcome.value = result.outcome;
        useSetCookie(cookieName.value, result.outcome, cookieTime.value);
        visible.value = false;
      });
    }
  }

  function onCancel() {
    outcome.value = 'dismissed';
    useSetCookie(cookieName.value, 'dismissed', cookieTime.value);
    visible.value = false;
  }

  onMounted(() => {
    const savedOutcome = useGetCookie(cookieName.value);

    if (savedOutcome === 'accepted' || savedOutcome === 'dismissed') {
      outcome.value = savedOutcome;
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  });
</script>
<template>
  <UiDrawer bottom v-model:open="open" class="pwa-install-prompt" aria-labelledby="pwa-install-prompt-title" aria-describedby="pwa-install-prompt-desc">
    <div class="pwa-install-prompt__container">
      <div class="pwa-install-prompt__icon">
        <SfIconBoxArrowDown aria-hidden="true" />
      </div>
      <div class="pwa-install-prompt__content">
        <SfHeading type="h2" v-html="$t('SHOP.COMMON.HEADER.APP_INSTALL')" id="pwa-install-prompt-title" />
        <p v-html="$t('SHOP.COMMON.MESSAGE.APP_INSTALL')" id="pwa-install-prompt-desc" />
      </div>
      <div class="pwa-install-prompt__actions">
        <SfButton primary @click="onInstall">
          {{ $t('SHOP.COMMON.ACTION.APP_INSTALL') }}
        </SfButton>
        <SfButton @click="onCancel">
          {{ $t('SHOP.COMMON.ACTION.CONTINUE_IN_BROWSER') }}
        </SfButton>
      </div>
    </div>
  </UiDrawer>
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  :root {
    --sf-pwa-install-prompt-icon-foreground: var(--primary-color);
    --sf-pwa-install-prompt-gap: 2rem;
  }

  .pwa-install-prompt {
    &__container {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      max-width: 1400px;
      margin: 0 auto;
      gap: var(--sf-pwa-install-prompt-gap);

      @include media.query(md) {
        flex-direction: column;
      }
    }

    &__icon {
      display: flex;
      flex: 0 0 auto;
      justify-content: center;
      align-items: center;
      font-size: 2rem;
      color: var(--sf-pwa-install-prompt-icon-foreground);
    }

    &__content {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      gap: calc(var(--sf-pwa-install-prompt-gap) / 2);

      h2 {
        font-size: 1rem;
        font-weight: var(--font-weight-bold);
      }

      p {
        font-size: 0.875rem;
      }
    }

    &__actions {
      display: flex;
      flex: 0 0 auto;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      gap: calc(var(--sf-pwa-install-prompt-gap) / 2);

      @include media.query(md) {
        flex-direction: column;
        align-items: stretch;
      }
    }
  }
</style>
